import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SessionService } from '@sh/services/auth/session.service';
import { ProfileService } from '@sh/services/profile/profile.service';
import { firstValueFrom } from 'rxjs';

/**
 * Orchestrates the destructive "delete my account" flow:
 * call the endpoint, then tear down the client session.
 */
@Injectable({ providedIn: 'root' })
export class AccountDeletionService {
  constructor(
    private profileService: ProfileService,
    private session: SessionService,
  ) {}

  /**
   * Anonymizes the current account, then signs out and routes to landing.
   *
   * Resolves on success (200) and on the already-gone cases (401/404) — both
   * mean the identity no longer exists, so we just clean up the client.
   * Rejects on server/network errors: the server transaction rolled back, so
   * the account is untouched and the caller should keep the user signed in and
   * allow a retry.
   */
  async deleteAccount(): Promise<void> {
    try {
      await firstValueFrom(this.profileService.deleteMyProfile());
    } catch (err) {
      if (
        err instanceof HttpErrorResponse &&
        (err.status === 401 || err.status === 404)
      ) {
        // Token invalid/expired or user already deleted — treat as logged out.
        await this.session.endSession();
        return;
      }
      throw err;
    }

    // Account is gone server-side — tear down the client session.
    await this.session.endSession();
  }
}
